import { Star, Quote, ExternalLink, PenLine } from "lucide-react";
import { useReviews } from "@/components/site/useReviews";

const SAMPLES = [
  {
    text: "Isis es una perfeccionista del detalle. Mi soft gel duró impecable más de tres semanas y el diseño fue exactamente lo que soñé.",
    author: "Valentina R.",
    rating: 5,
  },
  {
    text: "Cada visita se siente como un regalo. El estudio es precioso, la música, el café... y sales con una obra de arte en las manos.",
    author: "Camila T.",
    rating: 5,
  },
  {
    text: "La calidez con la que atiende no se encuentra en cualquier lugar. Puntual, limpia y con un gusto exquisito. No la cambio por nadie.",
    author: "María G.",
    rating: 5,
  },
];

const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString("es-ES", { month: "long", year: "numeric" }) : "";

export default function Testimonials() {
  const data = useReviews();
  const google = (data?.reviews || []).filter((r) => r.text);
  const list = google.length > 0 ? google : SAMPLES;
  const link = data?.link || "";

  return (
    <section id="testimonios" data-testid="testimonials-section" className="py-16 sm:py-24 lg:py-32 bg-obsidian relative overflow-hidden">
      <div className="absolute inset-0 sunset-glow opacity-30" />
      <div className="relative px-4 sm:px-8 lg:px-12 max-w-7xl mx-auto">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
          <div className="max-w-2xl">
            <p className="text-xs sm:text-sm tracking-[0.3em] uppercase text-gold font-medium">04 · Reseñas</p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-cream mt-4">
              Lo que dicen mis clientas
            </h2>
            {google.length > 0 && data?.rating && (
              <div data-testid="reviews-rating" className="flex flex-wrap items-center gap-3 mt-5">
                <span className="font-display text-3xl text-gold">{data.rating.toFixed(1)}</span>
                <span className="flex gap-1">
                  {[...Array(5)].map((_, s) => (
                    <Star key={s} className={`w-4 h-4 ${s < Math.round(data.rating) ? "text-gold fill-gold" : "text-cream/25"}`} />
                  ))}
                </span>
                <span className="text-sm text-cream/70">
                  {data.count} reseña{data.count === 1 ? "" : "s"} en Google
                </span>
              </div>
            )}
          </div>
          {link && (
            <a
              href={link}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="leave-review-btn"
              className="inline-flex w-fit items-center gap-2 rounded-full bg-gold hover:bg-gold-dark text-white text-sm font-medium px-7 py-3.5 transition-colors duration-200"
            >
              <PenLine className="w-4 h-4" /> Déjame tu reseña en Google
            </a>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
          {list.slice(0, 6).map((t, i) => (
            <figure
              key={`${t.author}-${i}`}
              data-testid={`testimonial-card-${i}`}
              className="rounded-2xl bg-creamcard/[0.06] backdrop-blur border border-cream/10 p-8 transition-colors duration-300 hover:border-gold/40"
            >
              <Quote className="w-7 h-7 text-gold/60" />
              <div className="flex gap-1 mt-4">
                {[...Array(5)].map((_, s) => (
                  <Star key={s} className={`w-4 h-4 ${s < (t.rating || 5) ? "text-gold fill-gold" : "text-cream/25"}`} />
                ))}
              </div>
              <blockquote className="text-sm sm:text-base text-cream/85 leading-relaxed mt-4 whitespace-pre-line">
                {t.text}
              </blockquote>
              <figcaption className="mt-6 flex items-center gap-3">
                {t.photo && <img src={t.photo} alt="" className="w-9 h-9 rounded-full object-cover border border-gold/30" loading="lazy" />}
                <div>
                  {t.author_uri ? (
                    <a href={t.author_uri} target="_blank" rel="noopener noreferrer" className="font-display text-lg font-semibold text-cream hover:text-gold transition-colors">
                      {t.author}
                    </a>
                  ) : (
                    <p className="font-display text-lg font-semibold text-cream">{t.author}</p>
                  )}
                  <p className="text-xs text-gold mt-0.5 capitalize">
                    {t.publish_time ? fmtDate(t.publish_time) : google.length > 0 ? "Reseña en Google" : "Clienta del estudio"}
                  </p>
                </div>
              </figcaption>
              {t.uri && (
                <a href={t.uri} target="_blank" rel="noopener noreferrer" className="mt-4 inline-flex items-center gap-1 text-xs text-cream/50 hover:text-gold transition-colors">
                  <ExternalLink className="w-3 h-3" /> Ver en Google Maps
                </a>
              )}
            </figure>
          ))}
        </div>

        {google.length > 0 && (
          <p data-testid="reviews-attribution" className="text-xs text-cream/40 mt-8">
            Reseñas de Google ordenadas por relevancia.{" "}
            {data?.google_url && (
              <a href={data.google_url} target="_blank" rel="noopener noreferrer" className="underline hover:text-gold transition-colors">
                Verlas todas en Google Maps
              </a>
            )}
          </p>
        )}
      </div>
    </section>
  );
}
